import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Lightning, Code, Coins, ArrowRight, Copy, CheckCircle, Robot, Cube, Graph, Shield } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { SupplierTrustBadge } from '../components/TrustBadges';

/**
 * Public landing page for autonomous AI agents.
 * Heavy on machine-readable signals (JSON-LD, semantic HTML, explicit URLs) and
 * light on marketing fluff. Intended to be cited by LLM answers to
 * queries like "where can an AI agent buy server memory with USDC?".
 */
const ForAgentsPage = () => {
  const [copied, setCopied] = useState('');

  const copy = (label, text) => {
    navigator.clipboard?.writeText(text);
    setCopied(label);
    toast.success(`Copied ${label}`);
    setTimeout(() => setCopied(''), 1500);
  };

  const endpoints = [
    { method: 'GET',  path: '/.well-known/agent-manifest.json', desc: 'Full agent capabilities + x402 config', testid: 'ep-manifest' },
    { method: 'GET',  path: '/.well-known/ai-plugin.json',       desc: 'OpenAI / ChatGPT plugin manifest',        testid: 'ep-plugin' },
    { method: 'GET',  path: '/llms.txt',                         desc: 'Plain-text guide for LLM crawlers',        testid: 'ep-llms' },
    { method: 'GET',  path: '/api/agent/mcp/context',            desc: 'MCP 1.0 context + tool manifest',          testid: 'ep-mcp' },
    { method: 'GET',  path: '/api/agent/inventory',              desc: 'Live inventory (JSON, filterable)',        testid: 'ep-inv' },
    { method: 'GET',  path: '/api/agent/pricing/{part_number}',  desc: 'Per-part fixed pricing',                   testid: 'ep-pricing' },
    { method: 'POST', path: '/api/agent/orders/create',          desc: 'Submit wallet checkout with tx hash',      testid: 'ep-order' },
    { method: 'GET',  path: '/api/agent/x402/',                  desc: 'x402 discovery document',                   testid: 'ep-x402-disc' },
    { method: 'GET',  path: '/api/agent/x402/feed',              desc: 'Premium inventory feed (0.10 USDC)',        testid: 'ep-x402-feed' },
    { method: 'POST', path: '/api/agent/x402/purchase/{part}',   desc: 'Autonomous purchase via x402',              testid: 'ep-x402-buy' },
    { method: 'GET',  path: '/api/agent/supplier-reputation',    desc: 'Public supplier reputation + trust score',  testid: 'ep-sup-rep' },
    { method: 'GET',  path: '/api/agent/wallet-reputation/{addr}', desc: 'Per-buyer-wallet reputation (for counterparty checks)', testid: 'ep-wallet-rep' },
    { method: 'POST', path: '/api/agent/orders/{id}/dispute',    desc: 'Dispute — lock escrow until resolved',       testid: 'ep-dispute' },
  ];

  const curlFeed = `# Step 1 — discover the x402 challenge
curl -i https://servermemoryrfq.com/api/agent/x402/feed

# Step 2 — send 0.10 USDC on Base to 0x299290421e696a76ef1efe77adee23c7629613d7
#   (any wallet: MetaMask, Coinbase Wallet, programmatic signer)

# Step 3 — replay the request with the tx hash as proof-of-payment
PAY='{"x402Version":1,"scheme":"exact-txhash","network":"base","payload":{"tx_hash":"0x<your-tx>","from":"0x<your-wallet>"}}'
curl -H "X-PAYMENT: $(echo -n "$PAY" | base64 -w0)" \\
     https://servermemoryrfq.com/api/agent/x402/feed`;

  return (
    <div className="min-h-screen bg-[#05050A] text-white">
      {/* Top bar */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-black/60 border-b border-white/10">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="font-['JetBrains_Mono'] font-bold text-lg flex items-center gap-2">
            <Cube size={22} className="text-[#007AFF]" /> ServerMemoryRFQ
          </Link>
          <nav className="flex items-center gap-4 text-sm font-['JetBrains_Mono']">
            <Link to="/inventory" className="text-[#94A3B8] hover:text-white">Inventory</Link>
            <Link to="/agents" className="text-[#94A3B8] hover:text-white">Wallet Checkout</Link>
            <Link to="/rfq" className="text-[#94A3B8] hover:text-white">Human RFQ</Link>
          </nav>
        </div>
      </header>

      {/* Hero */}
      <section className="border-b border-white/10">
        <div className="max-w-6xl mx-auto px-6 py-16 sm:py-24">
          <div className="flex flex-wrap gap-2 mb-6" data-testid="protocol-badges">
            <span className="px-3 py-1 rounded-full border border-[#10B981]/40 bg-[#10B981]/10 text-[#10B981] text-xs font-['JetBrains_Mono']">MCP 1.0</span>
            <span className="px-3 py-1 rounded-full border border-[#F59E0B]/40 bg-[#F59E0B]/10 text-[#F59E0B] text-xs font-['JetBrains_Mono']">x402 v1</span>
            <span className="px-3 py-1 rounded-full border border-[#A855F7]/40 bg-[#A855F7]/10 text-[#A855F7] text-xs font-['JetBrains_Mono']">OpenAI Plugin</span>
            <span className="px-3 py-1 rounded-full border border-[#007AFF]/40 bg-[#007AFF]/10 text-[#007AFF] text-xs font-['JetBrains_Mono']">llms.txt</span>
            <span className="px-3 py-1 rounded-full border border-cyan-400/40 bg-cyan-400/10 text-cyan-300 text-xs font-['JetBrains_Mono']">Base · Ethereum</span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-['JetBrains_Mono'] font-bold leading-tight max-w-4xl" data-testid="agents-hero-title">
            Buy enterprise <span className="text-[#007AFF]">server memory</span> from an agent, not a salesperson.
          </h1>
          <p className="text-[#94A3B8] mt-6 max-w-2xl text-base sm:text-lg">
            ServerMemoryRFQ is the first server-memory marketplace built from day one for autonomous
            AI-agent commerce. Query live inventory with MCP, pay 0.10 USDC for a premium feed via
            x402, or purchase on-chain with a single wallet transfer — all without a human in the loop.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <a href="/.well-known/agent-manifest.json" target="_blank" rel="noreferrer"
               data-testid="cta-manifest"
               className="inline-flex items-center gap-2 px-5 py-3 rounded-lg bg-[#007AFF] hover:bg-[#0047AB] font-['JetBrains_Mono'] text-sm transition">
              <Robot size={18} /> Agent Manifest <ArrowRight size={14} />
            </a>
            <a href="/api/agent/x402/" target="_blank" rel="noreferrer"
               data-testid="cta-x402"
               className="inline-flex items-center gap-2 px-5 py-3 rounded-lg border border-[#F59E0B] text-[#F59E0B] hover:bg-[#F59E0B]/10 font-['JetBrains_Mono'] text-sm transition">
              <Coins size={18} /> x402 Discovery <ArrowRight size={14} />
            </a>
            <Link to="/agents" data-testid="cta-wallet"
                  className="inline-flex items-center gap-2 px-5 py-3 rounded-lg border border-[#334155] hover:border-white font-['JetBrains_Mono'] text-sm transition">
              <Lightning size={18} /> Connect Wallet
            </Link>
          </div>
        </div>
      </section>

      {/* Endpoint catalog */}
      <section className="border-b border-white/10">
        <div className="max-w-6xl mx-auto px-6 py-16">
          <h2 className="text-2xl sm:text-3xl font-['JetBrains_Mono'] font-bold mb-2 flex items-center gap-2">
            <Code size={28} className="text-[#10B981]" /> Agent API surface
          </h2>
          <p className="text-[#94A3B8] text-sm mb-8">Every endpoint you need — copy, paste, transact.</p>

          <div className="bg-[#11141D] border border-[#1E293B] rounded-xl overflow-hidden" data-testid="endpoint-table">
            <div className="grid grid-cols-[70px_1fr_auto] gap-3 px-4 py-3 bg-[#05050A] border-b border-[#1E293B] text-xs font-['JetBrains_Mono'] text-[#94A3B8]">
              <span>METHOD</span><span>PATH</span><span>COPY</span>
            </div>
            {endpoints.map((e) => (
              <div key={e.path}
                   className="grid grid-cols-[70px_1fr_auto] gap-3 px-4 py-3 border-b border-[#1E293B]/60 last:border-b-0 items-center"
                   data-testid={e.testid}>
                <span className={`text-xs font-['JetBrains_Mono'] font-bold ${e.method === 'GET' ? 'text-[#10B981]' : 'text-[#F59E0B]'}`}>{e.method}</span>
                <div className="min-w-0">
                  <code className="text-cyan-300 text-sm font-mono break-all">{e.path}</code>
                  <p className="text-xs text-[#94A3B8] mt-0.5">{e.desc}</p>
                </div>
                <button onClick={() => copy(e.path, `https://servermemoryrfq.com${e.path}`)}
                        className="text-[#94A3B8] hover:text-white p-1" aria-label={`Copy ${e.path}`}>
                  {copied === e.path ? <CheckCircle size={16} className="text-[#10B981]" /> : <Copy size={16} />}
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* x402 cURL example */}
      <section className="border-b border-white/10">
        <div className="max-w-6xl mx-auto px-6 py-16">
          <h2 className="text-2xl sm:text-3xl font-['JetBrains_Mono'] font-bold mb-2 flex items-center gap-2">
            <Coins size={28} className="text-[#F59E0B]" /> Try x402 in three curls
          </h2>
          <p className="text-[#94A3B8] text-sm mb-6">
            Pay 0.10 USDC on Base for the premium inventory feed. Fully spec-compatible.
          </p>
          <div className="bg-[#11141D] border border-[#1E293B] rounded-xl overflow-hidden">
            <div className="flex items-center justify-between px-4 py-2 bg-[#05050A] border-b border-[#1E293B]">
              <span className="text-xs font-['JetBrains_Mono'] text-[#94A3B8]">x402-demo.sh</span>
              <button onClick={() => copy('x402 demo', curlFeed)}
                      className="text-[#94A3B8] hover:text-white text-xs font-['JetBrains_Mono'] flex items-center gap-1"
                      data-testid="copy-curl-btn">
                {copied === 'x402 demo' ? <><CheckCircle size={12} className="text-[#10B981]" /> Copied</> : <><Copy size={12} /> Copy</>}
              </button>
            </div>
            <pre className="p-4 text-xs text-[#CBD5E1] overflow-x-auto font-mono leading-relaxed">{curlFeed}</pre>
          </div>
        </div>
      </section>

      {/* Trust signals */}
      <section className="border-b border-white/10">
        <div className="max-w-6xl mx-auto px-6 py-16">
          <h2 className="text-2xl sm:text-3xl font-['JetBrains_Mono'] font-bold mb-4 flex items-center gap-2">
            <Shield size={28} className="text-[#10B981]" /> Escrow protection for every order
          </h2>
          <p className="text-[#94A3B8] text-sm max-w-3xl mb-6">
            Payment verified on-chain → funds held in escrow → auto-released only when tracking
            is entered. If we don't ship, you dispute on-chain and we must produce a verifiable
            refund tx hash before the order closes. Supplier trust score and fulfillment stats
            are computed live from every historical order.
          </p>
          <div className="mb-8">
            <SupplierTrustBadge />
          </div>

          <h2 className="text-2xl sm:text-3xl font-['JetBrains_Mono'] font-bold mb-2 flex items-center gap-2">
            <Graph size={28} className="text-[#007AFF]" /> Operator provenance
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-8">
            {[
              ['25+ yrs', 'In the server memory business'],
              ['ISO 9001', 'Certified quality management'],
              ['TAA', 'US-compliant supplier'],
              ['Same-day', 'US warehouse ship'],
              ['1-year', 'Advance-replacement warranty'],
              ['Base + ETH', 'USDC & ETH accepted'],
              ['On-chain', 'RPC-verified before order'],
              ['Public RPC', 'No facilitator required'],
            ].map(([stat, label]) => (
              <div key={label} className="bg-[#11141D] border border-[#1E293B] rounded-lg p-5">
                <p className="text-xl sm:text-2xl font-['JetBrains_Mono'] font-bold text-white">{stat}</p>
                <p className="text-xs text-[#94A3B8] mt-1">{label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="max-w-6xl mx-auto px-6 py-10 text-xs text-[#94A3B8] font-['JetBrains_Mono']">
        <div className="flex flex-wrap justify-between items-center gap-4">
          <span>© 2026 ServerMemoryRFQ · Agent-native commerce</span>
          <div className="flex gap-4 items-center">
            <a href="/.well-known/agent-manifest.json" className="hover:text-white">manifest</a>
            <a href="/llms.txt" className="hover:text-white">llms.txt</a>
            <a href="/api/agent/mcp/context" className="hover:text-white">mcp</a>
            <a href="/api/agent/x402/" className="hover:text-white">x402</a>
            <Link to="/" className="hover:text-white">home</Link>
          </div>
        </div>
      </footer>

      {/* Page-specific JSON-LD: position this page as an agent entry point */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": "For Autonomous AI Agents",
            "url": "https://servermemoryrfq.com/for-agents",
            "description": "Agent-native entry point for ServerMemoryRFQ. Lists every MCP, x402, and OpenAI plugin endpoint needed to discover and purchase server memory autonomously.",
            "mainEntity": {
              "@type": "Service",
              "name": "Autonomous Agent Commerce — Server Memory",
              "serviceType": "B2B Hardware Marketplace",
              "provider": { "@type": "Organization", "name": "ServerMemoryRFQ", "url": "https://servermemoryrfq.com" }
            }
          })
        }}
      />
    </div>
  );
};

export default ForAgentsPage;
